import { ComponentProps, useState, createContext, useContext, ReactNode, useEffect } from 'react';
import * as RCollapsible from '@radix-ui/react-collapsible';
import styles from './CollapsibleList.module.scss';
import { withStyles } from 'vopay';
import { MaterialSymbol } from 'vopay';

const cn = withStyles(styles);

type ListProps = {
  children: ReactNode;
  className?: string;
};

export default function List({ children, className }: ListProps) {
  return <div className={cn('collapsibleListContainer', className)}>{children}</div>;
}

type ItemContext = {
  open: boolean;
};

const ItemContext = createContext<ItemContext>({
  open: false,
});

type ItemProps = ComponentProps<typeof RCollapsible.Root>;

function Item({ className, children, ...props }: ItemProps) {
  const [open, setOpen] = useState<boolean>(props.defaultOpen ?? false);

  useEffect(() => {
    if (props.open !== undefined) setOpen(props.open);
  }, [props.open]);

  return (
    <RCollapsible.Root
      {...props}
      open={open}
      onOpenChange={(val) => {
        setOpen(val);
        props.onOpenChange?.(val);
      }}
      className={cn('collapsibleListItem', className)}
    >
      <ItemContext.Provider value={{ open }}>{children}</ItemContext.Provider>
    </RCollapsible.Root>
  );
}

List.Item = Item;

type TriggerProps = Omit<ComponentProps<typeof RCollapsible.Trigger>, 'children'> & {
  children: ReactNode | ((args: { open: boolean }) => ReactNode);
  'data-testid'?: string;
};

function Trigger({ children, className, 'data-testid': dataTestId, ...props }: TriggerProps) {
  const { open } = useContext(ItemContext);

  return (
    <RCollapsible.Trigger
      data-testid={dataTestId || 'collapsibleListTrigger'}
      {...props}
      className={cn('collapsibleTrigger', className)}
      asChild
    >
      <div
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;

          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.currentTarget.click();
          }
        }}
      >
        <div className={cn('chevron', open && 'open')}>
          <MaterialSymbol icon="keyboard_arrow_down" size="24px" />
        </div>
        {typeof children === 'function' ? children({ open }) : children}
      </div>
    </RCollapsible.Trigger>
  );
}

List.Trigger = Trigger;

type ContentProps = ComponentProps<typeof RCollapsible.Content>;

function Content({ className, children, ...props }: ContentProps) {
  return (
    <RCollapsible.Content {...props} className={cn('collapsibleContent', className)}>
      <div className={cn('collapsibleContentInner')}>{children}</div>
    </RCollapsible.Content>
  );
}

List.Content = Content;
